import { mix } from '../lib/color'
import { dayKey, weekKeys } from '../lib/dates'
import type { Theme } from '../theme'
import { STICKERS, STICKER_TOTAL } from '../../shared/stickers'
import { MASTERY_MAX, MAX_FACTOR, factKey, factLabel, type GameState } from './types'
import { DONE_PCT, tablePct, zoneUpto } from '../../shared/progress'

export interface TableStat {
  n: number
  pct: number
  color: string
}

export interface WeekBar {
  day: string
  height: number
  bg: string
  today: boolean
}

export interface TableMedal {
  n: number
  pct: number
  done: boolean
}

export interface StickerView {
  icon: string
  title: string
  hint: string
  bg: string
  locked: boolean
}

/** Доля таблицы (2..10 × 2..10), которая освоена. */
export function masteredPct(s: GameState): number {
  let total = 0
  let sum = 0
  for (let a = 2; a <= MAX_FACTOR; a++) {
    for (let b = 2; b <= MAX_FACTOR; b++) {
      total++
      sum += s.progress.mastery[factKey(a, b)] ?? 0
    }
  }
  return total === 0 ? 0 : Math.round((sum / (total * MASTERY_MAX)) * 100)
}

export function masteredFacts(s: GameState): number {
  return Object.values(s.progress.mastery).filter((v) => v >= MASTERY_MAX).length
}

export function level(s: GameState): number {
  return Math.floor(s.progress.xp / 100) + 1
}

export function xpInLevel(s: GameState): number {
  return s.progress.xp % 100
}

/** Сколько примеров решено за всё время. */
export function totalAnswers(s: GameState): number {
  return Object.values(s.progress.history).reduce((sum, d) => sum + d.answers, 0)
}

export function tableStats(s: GameState, theme: Theme): TableStat[] {
  const out: TableStat[] = []
  for (let n = 2; n <= MAX_FACTOR; n++) {
    let count = 0
    let sum = 0
    for (let b = 2; b <= MAX_FACTOR; b++) {
      count++
      sum += s.progress.mastery[factKey(n, b)] ?? 0
    }
    const pct = count === 0 ? 0 : Math.round((sum / (count * MASTERY_MAX)) * 100)
    out.push({ n, pct, color: pct > 66 ? theme.acc : pct > 33 ? theme.warn : theme.acc2 })
  }
  return out
}

/** Медали за таблицы 2..10: цель, до которой доходит и тот, кто учит одну цифру. */
export function tableMedals(s: GameState): TableMedal[] {
  const out: TableMedal[] = []
  for (let n = 2; n <= MAX_FACTOR; n++) {
    const pct = tablePct(s.progress, n)
    out.push({ n, pct, done: pct >= DONE_PCT })
  }
  return out
}

/** Самые слабые из уже встречавшихся фактов, худшие первыми. */
export function weakFacts(s: GameState, limit = 6): Array<{ key: string; label: string }> {
  return Object.keys(s.progress.mastery)
    .filter((k) => (s.progress.mastery[k] ?? 0) <= 1)
    .sort((x, y) => (s.progress.mastery[x] ?? 0) - (s.progress.mastery[y] ?? 0))
    .slice(0, limit)
    .map((k) => ({ key: k, label: factLabel(k) }))
}

export function weekBars(s: GameState, theme: Theme, dayNames: string[]): WeekBar[] {
  const keys = weekKeys()
  const today = dayKey()
  const seconds = keys.map((k) => s.progress.history[k]?.seconds ?? 0)
  const peak = Math.max(300, ...seconds) // пять минут задают базовый масштаб
  return keys.map((k, i) => ({
    day: dayNames[i] ?? '',
    height: Math.max(4, Math.round((seconds[i] / peak) * 78)),
    bg: k === today ? theme.acc : mix(theme.acc2, 0.5),
    today: k === today,
  }))
}

/** Играл или нет в каждый день недели — детская версия графика. */
export function weekPlayed(s: GameState): Array<{ day: number; played: boolean; today: boolean }> {
  const today = dayKey()
  return weekKeys().map((k, i) => ({
    day: i,
    played: (s.progress.history[k]?.answers ?? 0) > 0,
    today: k === today,
  }))
}

export function todaySeconds(s: GameState): number {
  return s.progress.history[dayKey()]?.seconds ?? 0
}

/** Все наклейки: открытые — своим значком, остальные — замком. */
export function stickers(s: GameState, theme: Theme): { list: StickerView[]; unlocked: number } {
  const stat = {
    masteredFacts: masteredFacts(s),
    streak: s.progress.streak,
    level: level(s),
    xp: s.progress.xp,
    zoneUpto: zoneUpto(s.progress),
  }
  const tint = { acc: theme.acc, acc2: theme.acc2, warn: theme.warn }
  let unlocked = 0
  const list = STICKERS.map((def) => {
    const locked = !def.test(stat)
    if (!locked) unlocked++
    return {
      icon: locked ? '🔒' : def.icon,
      title: def.title,
      hint: def.hint,
      bg: locked ? theme.card : mix(tint[def.tint], 0.5),
      locked,
    }
  })
  return { list, unlocked }
}

/** Четыре наклейки вокруг текущего рубежа — полоска для главного экрана. */
export function stickerStrip(list: StickerView[], unlocked: number): StickerView[] {
  const start = Math.max(0, Math.min(unlocked - 3, STICKER_TOTAL - 4))
  return list.slice(start, start + 4)
}

export function accuracy(s: GameState): number {
  const answered = s.right + s.wrong
  return answered === 0 ? 100 : Math.round((s.right / answered) * 100)
}

export function stars(s: GameState): number {
  const acc = accuracy(s)
  return acc >= 90 ? 3 : acc >= 70 ? 2 : 1
}

export { STICKER_TOTAL }
